import { ButtonAppearance, Icon, IconSize, Spinner, SpinnerSize, Text } from '@bechtel/gds';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { AIIcon } from './ai-icon';
import {
  AIAssistantCloseButtonStyled,
  AIAssistantContainerStyled,
  AIAssistantHeaderStyled,
  AIAssistantIframeContainerStyled,
  AIAssistantIframeStyled,
  AIAssistantLoadingStyled,
  AIAssistantPanelStyled,
  AIAssistantTriggerButtonStyled,
} from './styled';
import { AIAssistantContext, AIAssistantMessage, AIAssistantWidgetProps } from './types';
import { useAIButtonPosition } from './use-ai-button-position';

export const AIAssistantWidget: FC<AIAssistantWidgetProps> = ({
  assistantUrl,
  onExpand,
  onCollapse,
  initialExpanded = false,
  draggable = true,
}) => {
  const [isExpanded, setIsExpanded] = useState(initialExpanded);
  const [isLoading, setIsLoading] = useState(true); // Start loading immediately
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const contextRef = useRef<AIAssistantContext | null>(null);

  // Smart positioning hook
  const { position, isDragging, buttonRef, cyclePosition, handleDragStart } = useAIButtonPosition({
    draggable,
  });

  const handleToggle = useCallback(() => {
    if (isExpanded) {
      setIsExpanded(false);
      onCollapse?.();
    } else {
      setIsExpanded(true);
      onExpand?.();
    }
  }, [isExpanded, onExpand, onCollapse]);

  const handleClose = useCallback(() => {
    setIsExpanded(false);
    onCollapse?.();
  }, [onCollapse]);

  // Handle iframe load
  const handleIframeLoad = useCallback(() => {
    setIsLoading(false);

    // Send initial context when iframe loads
    if (contextRef.current && iframeRef.current?.contentWindow) {
      const message: AIAssistantMessage = {
        type: 'context-update',
        payload: contextRef.current,
      };
      iframeRef.current.contentWindow.postMessage(message, assistantUrl);
    }
  }, [assistantUrl]);

  // Method to update context (will be called by parent/provider)
  const updateContext = useCallback(
    (context: AIAssistantContext) => {
      contextRef.current = context;

      // Send context to iframe if it's loaded
      if (iframeRef.current?.contentWindow && !isLoading) {
        const message: AIAssistantMessage = {
          type: 'context-update',
          payload: context,
        };
        iframeRef.current.contentWindow.postMessage(message, assistantUrl);
      }
    },
    [assistantUrl, isLoading]
  );

  // Expose updateContext method via ref (for parent components to use)
  useEffect(() => {
    // This allows parent components to call updateContext
    (window as any).__aiAssistantUpdateContext = updateContext;

    return () => {
      delete (window as any).__aiAssistantUpdateContext;
    };
  }, [updateContext]);

  // Handle messages from iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Verify origin for security
      try {
        const messageUrl = new URL(assistantUrl);
        const eventUrl = new URL(event.origin);

        if (messageUrl.origin !== eventUrl.origin) {
          return;
        }
      } catch (error) {
        console.warn('Invalid URL in postMessage origin check:', error);
        return;
      }

      const message: AIAssistantMessage = event.data;

      switch (message.type) {
        case 'ready':
          // AI Assistant is ready, send current context
          if (contextRef.current && iframeRef.current?.contentWindow) {
            const contextMessage: AIAssistantMessage = {
              type: 'context-update',
              payload: contextRef.current,
            };
            iframeRef.current.contentWindow.postMessage(contextMessage, assistantUrl);
          }
          break;

        case 'resize':
          // Handle resize requests if needed
          break;

        default:
          break;
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [assistantUrl]);

  // Keyboard shortcut to toggle (Ctrl/Cmd + K)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        handleToggle();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleToggle]);

  return (
    <AIAssistantContainerStyled>
      {/* Trigger Button */}
      <div ref={buttonRef} style={{ position: 'fixed', zIndex: 1201 }}>
        <AIAssistantTriggerButtonStyled
          appearance={ButtonAppearance.PRIMARY}
          isExpanded={isExpanded}
          position={position}
          isDragging={isDragging}
          onClick={handleToggle}
          onDoubleClick={cyclePosition}
          onMouseDown={handleDragStart}
          aria-label="Open AI Assistant (Double-click to reposition, Alt+Drag to move)"
          data-testid="ai-assistant-trigger"
          title={
            isDragging
              ? 'Release to place here'
              : 'AI Assistant\n• Click: Open\n• Double-click: Change corner\n• Alt+Drag: Move anywhere'
          }
        >
          <AIIcon size={28} color="white" />
        </AIAssistantTriggerButtonStyled>
      </div>

      {/* Expandable Panel */}
      <AIAssistantPanelStyled isExpanded={isExpanded} data-testid="ai-assistant-panel">
        {/* Header */}
        <AIAssistantHeaderStyled>
          <Text size="500" weight="600" color="white" family="graphik">
            Enterprise AI Assistant
          </Text>
          <AIAssistantCloseButtonStyled
            appearance={ButtonAppearance.TERTIARY}
            onClick={handleClose}
            aria-label="Close AI Assistant"
            data-testid="ai-assistant-close"
          >
            <Icon icon="times" size={IconSize.MEDIUM} fillColor="white" />
          </AIAssistantCloseButtonStyled>
        </AIAssistantHeaderStyled>

        {/* Iframe Container */}
        <AIAssistantIframeContainerStyled>
          {isLoading && isExpanded && (
            <AIAssistantLoadingStyled>
              <Spinner size={SpinnerSize.LARGE} />
            </AIAssistantLoadingStyled>
          )}
          <AIAssistantIframeStyled
            ref={iframeRef}
            src={assistantUrl}
            title="AI Assistant"
            onLoad={handleIframeLoad}
            allow="clipboard-read; clipboard-write"
            sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
            data-testid="ai-assistant-iframe"
            style={{ display: isExpanded ? 'block' : 'none' }}
          />
        </AIAssistantIframeContainerStyled>
      </AIAssistantPanelStyled>
    </AIAssistantContainerStyled>
  );
};

export { AIIcon };
